import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Typography from '@mui/material/Typography'
import Modal from '@mui/material/Modal'
import { useState } from 'react'
import { SkipNextRounded } from '@mui/icons-material'
import { PopupState } from 'material-ui-popup-state/hooks'
import { useSkipTasksMutation } from '@reader_task_list_view/hooks/useSkipTasksMutation'
import { toast } from 'sonner'
import Toast from './Toast'
import { AxiosError } from 'axios'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { TextField } from '@common/components/Form/TextField'

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  alignSelf: 'stretch',
  borderRadius: 2,
  bgcolor: 'background.default',
  boxShadow: 8,
  padding: 1,
}

const SkipValidationMessage = 'Provide a reason why you need to skip the task'

const SkipValidationSchema = z.object({
  reason: z.string().min(1, SkipValidationMessage),
})

export default function SkipModal({
  read_task_id,
  read_pseudo_id,
  study_id,
  popupState,
}: {
  read_task_id: string
  read_pseudo_id: number
  study_id: string
  popupState: PopupState
}) {
  const [open, setOpen] = useState(false)
  const {
    control,
    watch,
    formState: { isValid },
    setError,
  } = useForm({ mode: 'onChange', defaultValues: { reason: '' }, resolver: zodResolver(SkipValidationSchema) })
  const reason = watch('reason')
  const handleClose = () => {
    setOpen(false)
    popupState.close()
  }
  const { mutateAsync: skipTask } = useSkipTasksMutation()

  const handleSkipTask = async (task_ids: string[], study_id: string, reason: string) => {
    if (isValid) {
      await skipTask({
        reason: reason,
        read_task_ids: task_ids,
        study_id: study_id,
      })
        .then(() => {
          toast(<Toast message="Your request was sent successfully" variant="success" data-testid="success-toast" />)
          handleClose()
        })
        .catch((error) => {
          toast(<Toast message={(error as AxiosError).message} variant="error" data-testid="error-toast" />)
        })
    } else {
      setError('reason', { message: SkipValidationMessage })
    }
  }

  return (
    <>
      <Box
        onClick={() => setOpen(true)}
        sx={{
          display: 'flex',
          justifyContent: 'start',
          width: '100%',
          gap: 1.5,
          alignItems: 'center',
        }}
        data-testid="skip-task-button"
      >
        <SkipNextRounded
          sx={{
            color: 'text.secondary',
          }}
        />
        <Typography variant="body1">Skip Task</Typography>
      </Box>
      <Modal open={open} data-testid="skip-modal">
        <Box sx={modalStyle}>
          <Box
            sx={{
              display: 'flex',
              padding: 2,
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 0.5,
              alignSelf: 'stretch',
              width: 600,
            }}
          >
            <Typography variant="h6" color="text.primary">
              {`Skip task №${read_pseudo_id}?`}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Briefly explain why you are unable to continue working on this task
            </Typography>
          </Box>
          <Box
            component="form"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              alignSelf: 'stretch',
              padding: 2,
            }}
            noValidate
            autoComplete="off"
          >
            <TextField
              name="reason"
              control={control}
              id="outlined-multiline-static"
              label="Provide a reason"
              multiline
              rows={6}
              fullWidth
              data-testid="reason-textfield"
            />
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              alignSelf: 'stretch',
              padding: 2,
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              sx={{
                bgcolor: 'secondary.main',
                color: 'secondary.contrastText',
              }}
              onClick={handleClose}
              data-testid="cancel-skip-task-button"
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
              onClick={() => handleSkipTask([read_task_id], study_id, reason)}
              data-testid="confirm-skip-task-button"
            >
              Skip task
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  )
}
